import { useEffect, useState } from "react";
import type { AdminDashboardDependencies } from "../../app/composition/create-admin-dashboard-dependencies";
import type { AdminDashboardLocale } from "../../shared/locale/admin-dashboard-locale";
import { hasAdminPermission } from "../../contracts/admin-permissions";
import {
  ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
  ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  type AdminDeviceKpi,
  type AdminFailedFlow,
  type AdminDeviceKpiQuery,
  type AdminProjectionFreshness,
} from "../../contracts/admin-read-models";
import {
  createIdleAdminScreenState,
  createLoadingAdminScreenState,
  type AdminScreenState,
} from "../../contracts/admin-screen-state";
import { createAdminPermissionDeniedState } from "../../shared/query/create-admin-permission-denied-state";
import { toAdminScreenErrorState } from "../../shared/query/to-admin-screen-error-state";
import { searchFailedFlows } from "../failed-flows/failed-flows-use-cases";
import { searchDeviceKpis } from "./device-kpis-use-cases";
import {
  createEmptyDeviceKpisViewModel,
  isDeviceKpiObservationLagging,
  pickLatestDeviceKpiObservedAt,
  toDeviceKpiProjectionGapEvidenceViewModel,
  toDeviceKpiProjectionGapRowViewModel,
  toDeviceKpisViewModel,
  type DeviceKpiProjectionGapRowViewModel,
  type DeviceKpisViewModel,
} from "./device-kpi-view-model";

export type DeviceKpiScreenModelDependencies = AdminDashboardDependencies & {
  locale?: AdminDashboardLocale;
  query?: AdminDeviceKpiQuery;
  skip?: boolean;
};

export type DeviceKpiScreenModel = AdminScreenState<DeviceKpisViewModel>;

function createDefaultDeviceKpiQuery(): AdminDeviceKpiQuery {
  return {
    timeRange: {
      from: ADMIN_FULL_HISTORY_TIME_RANGE_START_AT,
      to: new Date().toISOString(),
    },
    limit: ADMIN_LIST_QUERY_DEFAULT_LIMIT,
  };
}

function deriveFreshnessPriority(
  latestObservedAt: string | undefined,
  generatedAt: string,
  sourceFreshness: Record<string, AdminProjectionFreshness>,
  hasProjectionGapEvidence: boolean,
): "stale" | "partial" | "ready" {
  if (isDeviceKpiObservationLagging(latestObservedAt, generatedAt)) {
    return "stale";
  }

  if (hasProjectionGapEvidence) {
    return "partial";
  }

  const values = Object.values(sourceFreshness);

  if (values.includes("stale")) {
    return "stale";
  }

  if (values.includes("partial") || values.includes("unavailable")) {
    return "partial";
  }

  return "ready";
}

function createDependenciesKey(dependencies: DeviceKpiScreenModelDependencies): string {
  return JSON.stringify({
    roles: dependencies.adminRoles,
    query: dependencies.query ?? null,
  });
}

function hasDeviceKpiScopeFilters(query: AdminDeviceKpiQuery): boolean {
  return Boolean(query.deviceInstallId || query.release || query.appVersion || query.platform);
}

type DeviceKpiProjectionGapDiagnostics = {
  evidence: ReturnType<typeof toDeviceKpiProjectionGapEvidenceViewModel> | undefined;
  rows: DeviceKpiProjectionGapRowViewModel[];
  recentFlows: AdminFailedFlow[];
};

function buildDeviceKpiScopeKey(scope: {
  deviceInstallId: string;
  release?: string;
  appVersion?: string;
  platform?: string;
}): string {
  return [scope.deviceInstallId, scope.release ?? "-", scope.appVersion ?? "-", scope.platform ?? "-"].join("|");
}

function buildFailedFlowScopeKey(failedFlow: AdminFailedFlow): string | undefined {
  if (!failedFlow.deviceInstallId) {
    return undefined;
  }

  return buildDeviceKpiScopeKey({
    deviceInstallId: failedFlow.deviceInstallId,
    release: failedFlow.release,
    appVersion: failedFlow.appVersion,
    platform: failedFlow.platform,
  });
}

function patchDeviceKpiWithRecentFailedFlow(
  item: AdminDeviceKpi,
  failedFlow: AdminFailedFlow,
): AdminDeviceKpi {
  const itemTimestamp = Date.parse(item.lastSeenAt);
  const failedFlowTimestamp = Date.parse(failedFlow.createdAt);
  const shouldPromoteLastSeenAt =
    !Number.isNaN(failedFlowTimestamp) && (Number.isNaN(itemTimestamp) || failedFlowTimestamp > itemTimestamp);

  return {
    ...item,
    lastSeenAt: shouldPromoteLastSeenAt ? failedFlow.createdAt : item.lastSeenAt,
    topErrorCode: shouldPromoteLastSeenAt ? failedFlow.errorCode : item.topErrorCode ?? failedFlow.errorCode,
    latestUserIdHash: shouldPromoteLastSeenAt ? failedFlow.userIdHash ?? item.latestUserIdHash : item.latestUserIdHash,
    latestUserAccountIdentity:
      shouldPromoteLastSeenAt ? failedFlow.accountIdentity ?? item.latestUserAccountIdentity : item.latestUserAccountIdentity,
    latestRuntimeAppBuildNumber:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimeAppBuildNumber ?? item.latestRuntimeAppBuildNumber
        : item.latestRuntimeAppBuildNumber,
    latestRuntimeLocale:
      shouldPromoteLastSeenAt ? failedFlow.runtimeLocale ?? item.latestRuntimeLocale : item.latestRuntimeLocale,
    latestRuntimeTimezoneOffsetBucket:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimeTimezoneOffsetBucket ?? item.latestRuntimeTimezoneOffsetBucket
        : item.latestRuntimeTimezoneOffsetBucket,
    latestRuntimeTimezoneNameBucket:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimeTimezoneNameBucket ?? item.latestRuntimeTimezoneNameBucket
        : item.latestRuntimeTimezoneNameBucket,
    latestRuntimeDeviceModelBucket:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimeDeviceModelBucket ?? item.latestRuntimeDeviceModelBucket
        : item.latestRuntimeDeviceModelBucket,
    latestRuntimeDeviceModelNameBucket:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimeDeviceModelNameBucket ?? item.latestRuntimeDeviceModelNameBucket
        : item.latestRuntimeDeviceModelNameBucket,
    latestRuntimeOsMajorBucket:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimeOsMajorBucket ?? item.latestRuntimeOsMajorBucket
        : item.latestRuntimeOsMajorBucket,
    latestRuntimeOsVersionMinorBucket:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimeOsVersionMinorBucket ?? item.latestRuntimeOsVersionMinorBucket
        : item.latestRuntimeOsVersionMinorBucket,
    latestRuntimeNetworkTypeBucket:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimeNetworkTypeBucket ?? item.latestRuntimeNetworkTypeBucket
        : item.latestRuntimeNetworkTypeBucket,
    latestRuntimePhysicalDeviceBucket:
      shouldPromoteLastSeenAt
        ? failedFlow.runtimePhysicalDeviceBucket ?? item.latestRuntimePhysicalDeviceBucket
        : item.latestRuntimePhysicalDeviceBucket,
    latestRuntimeContextAt:
      shouldPromoteLastSeenAt ? failedFlow.createdAt : item.latestRuntimeContextAt ?? failedFlow.createdAt,
  };
}

function createDeviceKpiSourceOnlyFallbackItem(
  failedFlow: AdminFailedFlow,
): AdminDeviceKpi | undefined {
  if (!failedFlow.deviceInstallId) {
    return undefined;
  }

  return {
    deviceInstallId: failedFlow.deviceInstallId,
    platform: failedFlow.platform ?? "unknown",
    appVersion: failedFlow.appVersion,
    release: failedFlow.release,
    lastSeenAt: failedFlow.createdAt,
    sessionStartCount: 0,
    authSuccessCount: 0,
    petCreateSuccessCount: 0,
    petUpdateSuccessCount: 0,
    petDeleteSuccessCount: 0,
    petPhotoConfirmSuccessCount: 0,
    todayStatusSaveSuccessCount: 0,
    dailySpeechCaptureStartCount: 0,
    dailySpeechCaptureCancelCount: 0,
    dailyDraftCreateSuccessCount: 0,
    dailyDraftUpdateCount: 0,
    dailyDraftDiscardCount: 0,
    dailyRecordSaveSuccessCount: 0,
    dailyMediaUpdateSuccessCount: 0,
    healthFileUploadSuccessCount: 0,
    healthFileConfirmSuccessCount: 0,
    healthAnalysisStartCount: 0,
    healthRecordSaveSuccessCount: 0,
    receiptExtractCount: 0,
    receiptSaveSuccessCount: 0,
    receiptCategoryCreateSuccessCount: 0,
    receiptCategoryUpdateSuccessCount: 0,
    receiptCategoryDeleteSuccessCount: 0,
    socialSuccessCount: 0,
    chatSuccessCount: 0,
    communitySuccessCount: 0,
    carePlanSuccessCount: 0,
    rideRequestSuccessCount: 0,
    failureCount: 1,
    topErrorCode: failedFlow.errorCode,
    latestUserIdHash: failedFlow.userIdHash,
    latestUserAccountIdentity: failedFlow.accountIdentity,
    latestRuntimeAppBuildNumber: failedFlow.runtimeAppBuildNumber,
    latestRuntimeLocale: failedFlow.runtimeLocale,
    latestRuntimeTimezoneOffsetBucket: failedFlow.runtimeTimezoneOffsetBucket,
    latestRuntimeTimezoneNameBucket: failedFlow.runtimeTimezoneNameBucket,
    latestRuntimeDeviceModelBucket: failedFlow.runtimeDeviceModelBucket,
    latestRuntimeDeviceModelNameBucket: failedFlow.runtimeDeviceModelNameBucket,
    latestRuntimeOsMajorBucket: failedFlow.runtimeOsMajorBucket,
    latestRuntimeOsVersionMinorBucket: failedFlow.runtimeOsVersionMinorBucket,
    latestRuntimeNetworkTypeBucket: failedFlow.runtimeNetworkTypeBucket,
    latestRuntimePhysicalDeviceBucket: failedFlow.runtimePhysicalDeviceBucket,
    latestRuntimeContextAt: failedFlow.createdAt,
    latestPetProfileCompletionStep: failedFlow.requestProfileCompletionStep,
    latestPetAttemptCountBucket: failedFlow.requestAttemptCountBucket,
    latestPetSpeciesBucket: failedFlow.requestSpeciesBucket,
    latestPetBreedSelectionBucket: failedFlow.requestBreedSelectionBucket,
    latestPetContextAt:
      failedFlow.requestProfileCompletionStep || failedFlow.requestAttemptCountBucket || failedFlow.requestSpeciesBucket || failedFlow.requestBreedSelectionBucket
        ? failedFlow.createdAt
        : undefined,
    latestSocialTargetUserLookupKind: failedFlow.requestTargetUserLookupKind,
    latestSocialPublicPetCountBucket: failedFlow.requestPublicPetCountBucket,
    latestSocialProfileMutationFieldSetBucket: failedFlow.requestProfileMutationFieldSetBucket,
    latestSocialContextAt:
      failedFlow.requestTargetUserLookupKind || failedFlow.requestPublicPetCountBucket || failedFlow.requestProfileMutationFieldSetBucket
        ? failedFlow.createdAt
        : undefined,
    latestChatRoomKind: failedFlow.requestRoomKind,
    latestChatMessageLengthBucket: failedFlow.requestMessageLengthBucket,
    latestChatMembershipCountBucket: failedFlow.requestMembershipCountBucket,
    latestChatContextAt:
      failedFlow.requestRoomKind || failedFlow.requestMessageLengthBucket || failedFlow.requestMembershipCountBucket
        ? failedFlow.createdAt
        : undefined,
    latestReceiptExtractSourceBucket: failedFlow.requestReceiptExtractSourceBucket,
    latestReceiptImageByteSizeBucket: failedFlow.requestReceiptImageByteSizeBucket,
    latestReceiptExtractionConfidenceBucket: failedFlow.requestReceiptExtractionConfidenceBucket,
    latestReceiptManualOverride: failedFlow.requestReceiptManualOverride,
    latestReceiptCategoryMutationType: failedFlow.requestReceiptCategoryMutationType,
    latestReceiptContextAt:
      failedFlow.requestReceiptExtractSourceBucket ||
      failedFlow.requestReceiptImageByteSizeBucket ||
      failedFlow.requestReceiptExtractionConfidenceBucket ||
      typeof failedFlow.requestReceiptManualOverride === "boolean" ||
      failedFlow.requestReceiptCategoryMutationType
        ? failedFlow.createdAt
        : undefined,
    latestCarePlanScheduleRecurrenceBucket: failedFlow.requestScheduleRecurrenceBucket,
    latestCarePlanDosePresence: failedFlow.requestDosePresence,
    latestCarePlanOccurrenceActionKind: failedFlow.requestOccurrenceActionKind,
    latestCarePlanSnoozeWindowBucket: failedFlow.requestSnoozeWindowBucket,
    latestCarePlanContextAt:
      failedFlow.requestScheduleRecurrenceBucket ||
      failedFlow.requestDosePresence ||
      failedFlow.requestOccurrenceActionKind ||
      failedFlow.requestSnoozeWindowBucket
        ? failedFlow.createdAt
        : undefined,
    latestRideVisitLeadTimeBucket: failedFlow.requestRideVisitLeadTimeBucket,
    latestRideLongestAddressLengthBucket: failedFlow.requestRideLongestAddressLengthBucket,
    latestRideMemoProvided: failedFlow.requestRideMemoProvided,
    latestRideContextAt:
      failedFlow.requestRideVisitLeadTimeBucket ||
      failedFlow.requestRideLongestAddressLengthBucket ||
      typeof failedFlow.requestRideMemoProvided === "boolean"
        ? failedFlow.createdAt
        : undefined,
  };
}

function mergeDeviceKpisWithRecentFailedFlows(
  items: AdminDeviceKpi[],
  recentFlows: AdminFailedFlow[],
): AdminDeviceKpi[] {
  const merged = new Map<string, AdminDeviceKpi>();

  for (const item of items) {
    merged.set(buildDeviceKpiScopeKey(item), item);
  }

  for (const failedFlow of recentFlows) {
    const scopeKey = buildFailedFlowScopeKey(failedFlow);
    if (!scopeKey) {
      continue;
    }

    const existingItem = merged.get(scopeKey);
    if (existingItem) {
      merged.set(scopeKey, patchDeviceKpiWithRecentFailedFlow(existingItem, failedFlow));
      continue;
    }

    const fallbackItem = createDeviceKpiSourceOnlyFallbackItem(failedFlow);
    if (fallbackItem) {
      merged.set(scopeKey, fallbackItem);
    }
  }

  return [...merged.values()];
}

async function loadDeviceKpiProjectionGapDiagnostics(
  dependencies: DeviceKpiScreenModelDependencies,
  query: AdminDeviceKpiQuery,
  items: AdminDeviceKpi[],
  latestObservedAt: string | undefined,
): Promise<DeviceKpiProjectionGapDiagnostics> {
  const failedFlowEnvelope = await searchFailedFlows(
    dependencies.queryPort,
    {
      timeRange: query.timeRange,
      deviceInstallId: query.deviceInstallId,
      release: query.release,
      appVersion: query.appVersion,
      platform: query.platform,
      limit: 10,
    },
  );
  const latestObservedTimestamp = latestObservedAt ? Date.parse(latestObservedAt) : Number.NaN;
  const recentGapFlows = failedFlowEnvelope.data.items.filter((failedFlow) => {
    const latestFailedFlowTimestamp = Date.parse(failedFlow.createdAt);
    return (
      !Number.isNaN(latestFailedFlowTimestamp) &&
      (Number.isNaN(latestObservedTimestamp) || latestFailedFlowTimestamp > latestObservedTimestamp)
    );
  }).sort((left, right) => Date.parse(right.createdAt) - Date.parse(left.createdAt));
  const dedupedRecentGapFlows = recentGapFlows
    .filter((failedFlow, index, flows) => {
      const scopeKey = buildFailedFlowScopeKey(failedFlow);
      if (!scopeKey) {
        return false;
      }

      return flows.findIndex((candidate) => buildFailedFlowScopeKey(candidate) === scopeKey) === index;
    })
    .slice(0, 5);
  const existingScopeKeys = new Set(items.map((item) => buildDeviceKpiScopeKey(item)));
  const sourceOnlyRecentGapFlows = dedupedRecentGapFlows.filter((failedFlow) => {
    const scopeKey = buildFailedFlowScopeKey(failedFlow);
    return scopeKey ? !existingScopeKeys.has(scopeKey) : false;
  });
  const recentGapRows = sourceOnlyRecentGapFlows
    .map((failedFlow) => toDeviceKpiProjectionGapRowViewModel(failedFlow, dependencies.locale ?? "ko"));
  const latestGapFlow = recentGapFlows[0];

  return {
    evidence:
      latestGapFlow ? toDeviceKpiProjectionGapEvidenceViewModel(latestGapFlow, dependencies.locale ?? "ko") : undefined,
    rows: recentGapRows,
    recentFlows: dedupedRecentGapFlows,
  };
}

export async function resolveDeviceKpiScreenModel(
  dependencies: DeviceKpiScreenModelDependencies,
): Promise<DeviceKpiScreenModel> {
  if (!hasAdminPermission(dependencies.adminPermissions, "admin.device_kpis.read")) {
    return createAdminPermissionDeniedState(
      dependencies.locale ?? "ko",
      {
        en: "Device KPI read permission denied.",
        ko: "실기기 지표 조회 권한이 없습니다.",
      },
      "admin_device_kpis_permission_denied",
    );
  }

  try {
    const resolvedQuery = dependencies.query ?? createDefaultDeviceKpiQuery();
    const envelope = await searchDeviceKpis(
      dependencies.queryPort,
      resolvedQuery,
    );
    const rawLatestObservedAt = pickLatestDeviceKpiObservedAt(envelope.data.items);
    const projectionGapDiagnostics = await loadDeviceKpiProjectionGapDiagnostics(
      dependencies,
      resolvedQuery,
      envelope.data.items,
      rawLatestObservedAt,
    );
    const mergedItems = mergeDeviceKpisWithRecentFailedFlows(
      envelope.data.items,
      projectionGapDiagnostics.recentFlows,
    );
    const mergedPage = {
      ...envelope.data,
      items: mergedItems,
      totalCount: Math.max(envelope.data.totalCount ?? envelope.data.items.length, mergedItems.length),
    };
    const latestObservedAt = pickLatestDeviceKpiObservedAt(mergedItems);

    if (mergedItems.length === 0) {
      if (hasDeviceKpiScopeFilters(resolvedQuery) && projectionGapDiagnostics.evidence) {
        return {
          status: "partial",
          viewModel: createEmptyDeviceKpisViewModel(
            envelope.meta,
            resolvedQuery,
            dependencies.locale ?? "ko",
            projectionGapDiagnostics.evidence,
            projectionGapDiagnostics.rows,
            rawLatestObservedAt,
            projectionGapDiagnostics.recentFlows[0]?.createdAt,
          ),
        };
      }

      return {
        status: "empty",
      };
    }

    const viewModel = toDeviceKpisViewModel(
      mergedPage,
      envelope.meta,
      dependencies.locale ?? "ko",
      projectionGapDiagnostics.evidence,
      projectionGapDiagnostics.rows,
      rawLatestObservedAt,
      projectionGapDiagnostics.recentFlows[0]?.createdAt,
    );
    const freshnessPriority = deriveFreshnessPriority(
      latestObservedAt,
      envelope.meta.generatedAt,
      envelope.meta.sourceFreshness,
      Boolean(projectionGapDiagnostics.evidence),
    );

    if (freshnessPriority === "stale") {
      return {
        status: "stale",
        viewModel,
      };
    }

    if (freshnessPriority === "partial") {
      return {
        status: "partial",
        viewModel,
      };
    }

    return {
      status: "ready",
      viewModel,
    };
  } catch (error) {
    return toAdminScreenErrorState(dependencies.locale ?? "ko", error);
  }
}

export function useDeviceKpiScreenModel(
  dependencies: DeviceKpiScreenModelDependencies,
  refreshSignal?: number,
): DeviceKpiScreenModel {
  const [state, setState] = useState<DeviceKpiScreenModel>(createIdleAdminScreenState());
  const dependenciesKey = createDependenciesKey(dependencies);

  useEffect(() => {
    if (dependencies.skip) {
      setState(createIdleAdminScreenState());
      return;
    }

    let cancelled = false;

    setState((current) => createLoadingAdminScreenState(current.viewModel));

    void resolveDeviceKpiScreenModel(dependencies).then((nextState) => {
      if (!cancelled) {
        setState(nextState);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [dependencies.queryPort, dependencies.skip, dependenciesKey, refreshSignal]);

  return state;
}
